const TOKEN_REGEX = /{{\s*[\w.]+\s*}}/g;
const STYLE_TAG_REGEX = /<style[\s\S]*?>[\s\S]*?<\/style>/gi;
const EMAIL_STYLES = new Set(['plain', 'branded', 'custom']);
const EMAIL_FONT_STACK = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Helvetica, Arial, sans-serif";

const normalizeHexColor = (value, fallback = '#4f46e5') => {
  const raw = String(value || '').trim();
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(raw)) {
    if (raw.length === 4) {
      return `#${raw[1]}${raw[1]}${raw[2]}${raw[2]}${raw[3]}${raw[3]}`.toLowerCase();
    }
    return raw.toLowerCase();
  }
  return fallback;
};

export const resolveTemplateEmailOptions = (template = {}, settings = {}) => {
  const fromName = String(template.fromName || template.from_name || settings?.from_name || '').trim();
  const fromEmail = String(template.fromEmail || template.from_email || settings?.from_email || '').trim();
  const replyTo = String(template.replyTo || template.reply_to || fromEmail || settings?.reply_to || '').trim();
  const styleRaw = String(template.emailStyle || template.email_style || 'plain').trim().toLowerCase();
  const emailStyle = EMAIL_STYLES.has(styleRaw) ? styleRaw : 'plain';
  const accentColor = normalizeHexColor(template.accentColor || template.accent_color);
  const brandLabel = String(template.brandLabel || template.brand_label || fromName || '').trim();
  const includeSignature = Boolean(template.includeSignature ?? template.include_signature);
  const signature = String(template.signature || '').trim();
  const customCss = String(template.customCss || template.custom_css || '').trim();

  return {
    fromName,
    fromEmail,
    replyTo,
    emailStyle,
    accentColor,
    brandLabel,
    includeSignature,
    signature,
    customCss
  };
};

/** Matches server/src/utils/template.js `.mr-email-content` rules for editor preview parity. */
const BASE_CONTENT_STYLES = (accentColor = '#4f46e5') => `
  .mr-email-content {
    font-size: 16px;
    line-height: 1.65;
    letter-spacing: 0.011em;
    color: #0f172a;
    font-family: ${EMAIL_FONT_STACK};
  }
  .mr-email-content * {
    box-sizing: border-box;
  }
  .mr-email-content p {
    margin: 0 0 18px;
  }
  .mr-email-content p:last-child {
    margin-bottom: 0;
  }
  .mr-email-content h1,
  .mr-email-content h2,
  .mr-email-content h3 {
    margin: 0 0 12px;
    line-height: 1.35;
    color: #0f172a;
    font-weight: 600;
  }
  .mr-email-content h1 { font-size: 22px; }
  .mr-email-content h2 { font-size: 19px; }
  .mr-email-content h3 { font-size: 17px; }
  .mr-email-content a {
    color: ${accentColor};
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  .mr-email-content strong {
    color: #0f172a;
    font-weight: 600;
  }
  .mr-email-content em {
    font-style: italic;
  }
  .mr-email-content ul,
  .mr-email-content ol {
    margin: 0 0 18px;
    padding-left: 24px;
  }
  .mr-email-content li {
    margin: 0 0 8px;
    line-height: 1.6;
  }
  .mr-email-content li:last-child {
    margin-bottom: 0;
  }
  .mr-email-content .wrapper {
    width: 100%;
    margin: 0 auto;
    max-width: 520px;
  }
  .mr-email-content .container {
    background: #eef2ff;
    border-radius: 12px;
    padding: 22px 26px;
    border: 1px solid #e2e8f0;
  }
  .mr-email-content .lead {
    font-size: 18px;
    font-weight: 600;
    color: #0f172a;
    line-height: 1.45;
  }
  .mr-email-content .muted {
    color: #475569;
  }
  .mr-email-content .section-gap {
    margin-top: 24px;
  }
  .mr-email-content .bullet-list {
    list-style-type: disc;
    margin: 14px 0 18px;
    padding-left: 26px;
  }
  .mr-email-content .cta-note {
    margin: 24px 0;
    padding: 18px 20px;
    border-radius: 12px;
    background: #eef2ff;
    border: 1px solid #e2e8f0;
  }
  .mr-email-content .signature {
    font-weight: 600;
  }
`;

const hasHtmlContent = (value = '') => {
  const withoutTokens = value.replace(TOKEN_REGEX, '');
  return /<\/?[a-z][\s\S]*>/i.test(withoutTokens);
};

const escapeHtml = (value = '') =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const buildPreviewText = (value = '') => value.replace(/\s+/g, ' ').trim().slice(0, 140);

const stripOuterHtmlWrappers = (value = '') => {
  if (!value) return value;
  const bodyMatch = value.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  const extractedBody = bodyMatch ? bodyMatch[1] : value;
  const htmlMatch = extractedBody.match(/<html[^>]*>([\s\S]*?)<\/html>/i);
  return htmlMatch ? htmlMatch[1] : extractedBody;
};

const extractStyleBlocks = (value = '') => {
  const styles = [];
  const content = value.replace(STYLE_TAG_REGEX, (match) => {
    const inner = match.replace(/^<style[^>]*>/i, '').replace(/<\/style>$/i, '');
    styles.push(inner.trim());
    return '';
  });
  return { styles, content };
};

const wrapContentWithDefaults = (content = '') => {
  if (!content) return '';
  return `<div class="mr-email-content">${content}</div>`;
};

const formatBodyAsHtml = (body = '') => {
  if (!body) return '';

  if (hasHtmlContent(body)) {
    return body;
  }

  const escaped = escapeHtml(body).replace(/\t/g, '    ');

  return `<div style="white-space:pre-wrap; line-height:1.65; margin:0 0 16px; color:#0f172a;">${escaped}</div>`;
};

const wrapPreviewShell = (contentHtml, options, customStyles = []) => {
  const accent = options.accentColor || '#4f46e5';
  const styleBlock = `<style>${BASE_CONTENT_STYLES(accent)}${customStyles.length ? `\n${customStyles.join('\n')}` : ''}${options.customCss ? `\n${options.customCss}` : ''}</style>`;
  const signatureBlock = options.includeSignature
    ? options.signature
      ? `<div style="margin-top:24px;padding-top:16px;border-top:1px solid #e2e8f0;">${options.signature}</div>`
      : options.fromName
        ? `<div style="margin-top:24px;padding-top:16px;border-top:1px solid #e2e8f0;font-weight:600;">${options.fromName}</div>`
        : ''
    : '';
  const unsubscribe = `<div style="margin-top:20px;padding-top:12px;border-top:1px solid #e5e7eb;font-size:12px;color:#9ca3af;">Unsubscribe</div>`;

  if (options.emailStyle === 'branded') {
    return `${styleBlock}
      <div style="background:#f1f5f9;padding:16px;border-radius:14px;">
        <div style="background:#ffffff;border:1px solid #e2e8f0;border-radius:12px;overflow:hidden;">
          <div style="height:4px;background:${accent};"></div>
          ${options.brandLabel ? `<div style="padding:16px 20px 0;font-size:12px;font-weight:600;letter-spacing:0.04em;text-transform:uppercase;color:#64748b;">${options.brandLabel}</div>` : ''}
          <div style="padding:12px 20px 20px;">${contentHtml}${signatureBlock}</div>
          <div style="background:#f8fafc;padding:12px 20px;font-size:12px;color:#64748b;">Unsubscribe</div>
        </div>
      </div>`;
  }

  if (options.emailStyle === 'custom') {
    return `${styleBlock}<div>${contentHtml}${signatureBlock}${unsubscribe}</div>`;
  }

  return `${styleBlock}<div style="background:#ffffff;padding:8px 0;font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#222;">${contentHtml}${signatureBlock}</div>`;
};

const buildPreviewUnsubscribeUrl = (contact = {}) => {
  const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000/api';
  const origin = String(apiBase).replace(/\/api\/?$/, '');
  const params = new URLSearchParams();
  if (contact?.id) params.set('id', contact.id);
  if (contact?.email) params.set('email', contact.email);
  const query = params.toString();
  return `${origin}/unsubscribe${query ? `?${query}` : ''}`;
};

/**
 * Render template by replacing tokens with actual values
 */
export const renderTemplate = (template = {}, contact = {}, settings = {}) => {
  let rendered = template.body || '';
  const options = resolveTemplateEmailOptions(template, settings);
  
  const tokens = {
    first_name: contact?.firstName || '',
    last_name: contact?.lastName || '',
    email: contact?.email || '',
    phone: contact?.phone || '',
    company_name: settings?.company_name || process.env.REACT_APP_COMPANY_NAME || 'Your Company',
    domain: contact?.email?.split('@')[1] || '',
    unsubscribe_link: buildPreviewUnsubscribeUrl(contact),
    product_link: `https://${settings?.sending_domain || process.env.REACT_APP_SENDING_DOMAIN}/product`,
    original_sender: contact?.email || ''
  };

  // Replace tokens in body
  Object.entries(tokens).forEach(([key, value]) => {
    rendered = rendered.replace(new RegExp(`{{${key}}}`, 'g'), value);
  });

  const strippedBody = stripOuterHtmlWrappers(rendered);
  const { styles: customStyles, content: bodyWithoutStyles } = extractStyleBlocks(strippedBody);
  const formattedBody = formatBodyAsHtml(bodyWithoutStyles);
  const contentHtml = wrapContentWithDefaults(formattedBody);
  const renderedSignature = options.signature
    ? Object.entries(tokens).reduce(
      (value, [key, tokenValue]) => value.replace(new RegExp(`{{${key}}}`, 'g'), tokenValue),
      options.signature
    )
    : '';
  const renderedHtml = wrapPreviewShell(contentHtml, { ...options, signature: renderedSignature }, customStyles);

  let previewText = template.previewText || template.preview_text || '';
  if (previewText) {
    Object.entries(tokens).forEach(([key, value]) => {
      previewText = previewText.replace(new RegExp(`{{${key}}}`, 'g'), value);
    });
    previewText = previewText.trim();
  } else {
    previewText = buildPreviewText(rendered);
  }

  // Replace tokens in subject if email template
  if (template.subject) {
    let renderedSubject = template.subject;
    Object.entries(tokens).forEach(([key, value]) => {
      renderedSubject = renderedSubject.replace(new RegExp(`{{${key}}}`, 'g'), value);
    });
    return {
      subject: renderedSubject,
      body: rendered,
      html: renderedHtml,
      previewText,
      fromName: options.fromName,
      fromEmail: options.fromEmail,
      replyTo: options.replyTo
    };
  }

  return {
    body: rendered,
    html: renderedHtml,
    previewText,
    fromName: options.fromName,
    fromEmail: options.fromEmail,
    replyTo: options.replyTo
  };
};

/**
 * Purify template to check for spam triggers and compliance issues
 * Returns issues, warnings, and a deliverability score (0-100)
 */
export const purifyTemplate = (template, settings) => {
  const issues = [];
  const warnings = [];
  
  // Subject line checks (email only)
  if (template.type === 'email' && template.subject) {
    // All caps check
    if (template.subject.toUpperCase() === template.subject && template.subject.length > 10) {
      issues.push('Subject is all caps - reduces open rates by 30%');
    }
    
    // Excessive punctuation
    if (template.subject.includes('!!!') || template.subject.includes('???')) {
      issues.push('Excessive punctuation in subject');
    }
    
    // Length checks
    if (template.subject.length > 60) {
      warnings.push('Subject over 60 chars - may be truncated on mobile');
    }
    if (template.subject.length < 15) {
      warnings.push('Subject under 15 chars - may seem incomplete');
    }
  }

  // Spam trigger words
  const spamWords = [
    'FREE', 'CLICK NOW', 'LIMITED TIME', 'ACT NOW', 'URGENT', 
    '100%', 'GUARANTEE', 'NO COST', 'WINNER', 'CONGRATULATIONS'
  ];
  const foundSpam = spamWords.filter(word => 
    template.body?.toUpperCase().includes(word)
  );
  if (foundSpam.length > 0) {
    issues.push(`Spam trigger words: ${foundSpam.join(', ')}`);
  }

  // Link density check
  const linkCount = (template.body?.match(/http/g) || []).length;
  if (linkCount > 5) {
    issues.push(`Too many links (${linkCount}) - keep under 5`);
  } else if (linkCount > 3) {
    warnings.push(`High link count (${linkCount}) - consider reducing`);
  }

  // Unsubscribe is added by the email shell; only flag custom HTML that omitted it.
  if (template.type === 'email' && (template.emailStyle || template.email_style) === 'custom' && !template.body?.toLowerCase().includes('unsubscribe')) {
    warnings.push('Custom HTML emails should include an unsubscribe link for CAN-SPAM');
  }

  // Personalization check
  const hasPersonalization = template.body?.includes('{{') && template.body?.includes('}}');
  if (!hasPersonalization) {
    warnings.push('No personalization tokens - may reduce engagement');
  }

  // SMS length check
  if (template.type === 'sms' && template.body?.length > 160) {
    warnings.push(`SMS over 160 chars (${template.body.length}) - will be sent as multiple messages`);
  }

  // Email body length check
  if (template.type === 'email' && template.body?.length < 50) {
    warnings.push('Email body very short - may seem incomplete');
  }

  // Sender email check
  const senderEmail = template.fromEmail || template.from_email || settings?.from_email || '';
  if (template.type === 'email' && senderEmail.includes('noreply')) {
    warnings.push('Using "noreply" sender - reduces deliverability and engagement');
  }

  if (template.type === 'email' && !senderEmail) {
    warnings.push('No from email set on this template - add one so sends are not signed by a default address');
  }

  if (template.type === 'email' && /tradeitsolutions/i.test(`${template.fromName || ''} ${senderEmail}`)) {
    warnings.push('This sender still uses TradeIT branding. Set your own from name and from email.');
  }

  // Calculate score: 100 base - 15 per issue - 5 per warning
  const score = Math.max(0, 100 - (issues.length * 15) - (warnings.length * 5));

  return { issues, warnings, score };
};

/**
 * Get available tokens for template editor
 */
export const getAvailableTokens = () => {
  return [
    { token: '{{first_name}}', description: 'Contact first name' },
    { token: '{{last_name}}', description: 'Contact last name' },
    { token: '{{email}}', description: 'Contact email' },
    { token: '{{phone}}', description: 'Contact phone' },
    { token: '{{company_name}}', description: 'Your company name' },
    { token: '{{domain}}', description: 'Contact email domain' },
    { token: '{{unsubscribe_link}}', description: 'Unsubscribe URL' },
    { token: '{{product_link}}', description: 'Product URL' }
  ];
};